import React from "react";
import {observer} from "mobx-react-lite";
import {useSitStores} from "~/stores";
import type {ComplexityTier, ExecutionStatus, Workstream} from "../../../ApiTypes";
import {ComplexityTierValues, ExecutionStatusValues, WorkstreamValues} from "../../../ApiTypes";
import {complexityTierLabel, executionStatusLabel, sitFieldLabel, workstreamLabel} from "../../../sitLabels";
import {SitFarolPill, SitField, SitHelp, SitRead, SitRichText} from "../../../components/design/SitDesignAtoms";
import i18n from "../../../../i18n/i18n";

/** A full-width field around a rich-text editor (not a <label>: clicks inside the editor must stay in it). */
const SitRichField: React.FC<{label: string; help?: string; children: React.ReactNode}> = ({label, help, children}) => (
    <div className="sitf full sitf-rich">
        <span className="sitf-label">{label}{help && <SitHelp text={help}/>}</span>
        {children}
    </div>
);

export const InitiativeGeneralForm: React.FC = observer(() => {
    const {ccStore, initiativeInstanceStore: s} = useSitStores();
    const dis = s.disabled;
    const tier = s.instance.complexity_tier;

    return (
        <>
            <div className="sit-grid">
                {ccStore.showCustomerId &&
                <SitField label={sitFieldLabel('customerId')}>
                    <input value={s.instance.customer_id ?? ''} disabled={dis}
                           onChange={e => s.customerId = e.target.value}/>
                </SitField>}
                <SitRichField label="Initiative description">
                    <SitRichText label="Initiative description" value={s.description.instance?.description ?? ''}
                                 disabled={dis} resetKey={`d-${s.load_count}-${dis}`}
                                 onChange={v => s.description.description = v}/>
                </SitRichField>
                <SitRichField label="Confidential description" help="Only visible to the owner, team and Finance.">
                    <SitRichText label="Confidential description"
                                 value={s.description.instance?.confidential_description ?? ''}
                                 disabled={dis} resetKey={`c-${s.load_count}-${dis}`}
                                 onChange={v => s.description.confidential_description = v}/>
                </SitRichField>
                <SitRichField label="Notes">
                    <SitRichText label="Notes" value={s.description.instance?.notes ?? ''}
                                 disabled={dis} resetKey={`n-${s.load_count}-${dis}`}
                                 onChange={v => s.description.notes = v}/>
                </SitRichField>
                <SitField label={sitFieldLabel('leverGroup')} req>
                    <select value={s.lever_group_id ?? ''} disabled={dis}
                            onChange={e => s.leverGroupId = Number(e.target.value)}>
                        {ccStore.leverGroupRM.result?.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                    </select>
                </SitField>
                <SitField label="Lever" req>
                    <select value={s.instance.lever_id ?? ''} disabled={dis}
                            onChange={e => s.leverId = Number(e.target.value)}>
                        {s.leverChoices.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
                    </select>
                </SitField>
                <SitRead label="Lever description" value={s.leverDescription || '—'}/>
                <SitField label={sitFieldLabel('complexityTier')}
                          help="How hard the initiative is to realise. Complex and Strategic initiatives need a working group and a committee presentation to move to the next stage.">
                    <select value={tier ?? ''} disabled={dis}
                            onChange={e => { if (e.target.value) s.complexityTier = Number(e.target.value) as ComplexityTier; }}>
                        <option value="">—</option>
                        {ComplexityTierValues.map(v => <option key={v} value={v}>{complexityTierLabel(v)}</option>)}
                    </select>
                </SitField>
                <SitField label={sitFieldLabel('workstream')}>
                    <select value={s.instance.workstream ?? ''} disabled={dis}
                            onChange={e => { if (e.target.value) s.workstream = Number(e.target.value) as Workstream; }}>
                        <option value="">—</option>
                        {WorkstreamValues.map(v => <option key={v} value={v}>{workstreamLabel(v)}</option>)}
                    </select>
                </SitField>
                <SitField label={sitFieldLabel('executionStatus')}>
                    <select value={s.instance.execution_status ?? ''} disabled={dis}
                            onChange={e => { if (e.target.value) s.executionStatus = Number(e.target.value) as ExecutionStatus; }}>
                        <option value="">—</option>
                        {ExecutionStatusValues.map(v => <option key={v} value={v}>{executionStatusLabel(v)}</option>)}
                    </select>
                </SitField>
            </div>

            {tier && (
                <div className="sit-farol-note">
                    <SitFarolPill tier={tier}/>
                    <span>{i18n.t(`farolNote.${tier}`, {ns: 'sit', defaultValue: ''})}</span>
                </div>
            )}

            <div className="sit-checks">
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.confidential} disabled={dis}
                                                    onChange={() => s.confidential = !s.instance.confidential}/>Confidential</label>
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.nda_required} disabled={dis}
                                                    onChange={() => s.nda_required = !s.instance.nda_required}/>Under NDA</label>
                <span className="sit-check-sep">Special project:</span>
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.sustainability} disabled={dis}
                                                    onChange={() => s.sustainability = !s.instance.sustainability}/>Sustainability</label>
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.risk_reduction} disabled={dis}
                                                    onChange={() => s.riskReduction = !s.instance.risk_reduction}/>Risk reduction</label>
                <label className="sit-check"><input type="checkbox" checked={!!s.instance.compliance} disabled={dis}
                                                    onChange={() => s.compliance = !s.instance.compliance}/>Compliance</label>
            </div>
        </>
    );
});
